import { ChangeDetectorRef, Component, OnInit, Input, ChangeDetectionStrategy } from '@angular/core';
import { Platform, ModalController, AlertController } from '@ionic/angular/lazy';
import { CartService } from 'src/app/services/cart.service';
import { StorageService } from 'src/app/services/storage.service';
import { ProductService } from 'src/app/services/product.service';
import { EventService } from 'src/app/services/event.service';
import { PermissionService } from 'src/app/services/permission.service';
import { OrganisationService } from 'src/app/services/organisation.service';
import { Decimal } from 'decimal.js';
import { Capacitor } from '@capacitor/core';
import { Network } from '@capacitor/network';

@Component({
    selector: 'app-cart-edit-item',
    templateUrl: './cart-edit-item.page.html',
    styleUrls: ['./cart-edit-item.page.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class CartEditItemPage implements OnInit {

    on_device = false;
    @Input() item_id = null;
    item = null;
    cart = null;
    item_sell_price = null;
    item_quantity = null;
    item_discount_amount = null;
    is_donation_or_voucher = false;
    segment = "";
    auto_hide_price_breakdown = true;
    save_contract_price = false;
    is_saving_contract_price = false;
    is_online = false;

    initial_item = null;

    constructor(
        public modalController: ModalController,
        public alertController: AlertController,
        private cartService: CartService,
        private storageService: StorageService,
        private productService: ProductService,
        private platform: Platform,
        private eventService: EventService,
        private organisationService: OrganisationService,
        public permissionService: PermissionService
    ) { }

    ngOnInit() {

        if(Capacitor.isNativePlatform()) {
            this.on_device = true;
        }

        this.cartService.get().then((cart) => {
            if (cart) {
                this.cart = cart;
                for (let i = 0; i < this.cart.items.length; i++) {
                    if (this.cart.items[i].id == this.item_id) {

                        this.initial_item = structuredClone(this.cart.items[i]);
                        this.item = this.cart.items[i];

                        this.item_sell_price = this.cart.items[i].sell_price.toFixed(2);
                        this.item_quantity = this.cart.items[i].quantity;
                        this.item_discount_amount = this.cart.items[i].discount_amount;

                        if(this.item.discount_type == 'fixed_amount') {
                            this.item_discount_amount = this.cart.items[i].discount_amount.toFixed(2);
                        }
                        else {
                            this.item_discount_amount = this.cart.items[i].discount_amount;
                        }

                        if (this.cart.items[i].type == 'donation_campaign' || this.cart.items[i].type == 'gift_voucher') {
                            this.is_donation_or_voucher = true;
                        }

                        this.calculatePricing();

                    }
                }
            }
        });

        // set default segment view
        this.segment = 'pricing';

        this.storageService.get("auto_hide_price_breakdown").then((auto_hide_price_breakdown) => {
            this.auto_hide_price_breakdown = auto_hide_price_breakdown;
        })

        Network.getStatus().then((network_status) => {
            this.is_online = network_status.connected;
        })
        .catch(() => {});

    }

    canSaveContractPrice() {
        return !!(this.cart?.person && this.cart?.organisation && !this.is_donation_or_voucher && (this.item?.type == 'product' || this.item?.type == 'product_variant') && this.permissionService.isPermitted('pos-sell-edit-item-save-contract-price') && this.is_online && this.item.sell_price !== null && this.initial_item.sell_price != this.item.sell_price && (!this.cart.organisation.contract_prices?.[this.cart.currency?.id]?.[this.item.id] || this.cart.organisation.contract_prices[this.cart.currency.id][this.item.id].type == 'fixed_price'));
    }
    sanitiseItemSellPrice() {

        let item_sell_price = this.item_sell_price.toString();

        // only accept numbers/decimal points
        if(/[^0-9.]/.test(item_sell_price)) {
            this.item.sell_price = null;
        }
        else if(item_sell_price.startsWith('.')) {
            this.item.sell_price = null;
        }
        else if(item_sell_price.endsWith('.')) {
            this.item.sell_price = null;
        }
        else if(item_sell_price.split('.').length > 2) {
            this.item.sell_price  = null;
        }
        else if(!item_sell_price.length) {
            this.item.sell_price  = null;
        }
        else {

            this.item.sell_price = Number(new Decimal(item_sell_price).toDecimalPlaces(2));

            // NOTE: Set custom_price if the sell price changes, unless the item is a donation or gift voucher.
            if(this.initial_item.sell_price != this.item.sell_price) {
                if(!this.is_donation_or_voucher) {
                    this.item['has_contract_price'] = false;
                    this.item['has_volume_discount'] = false;
                    this.item['has_custom_price'] = true;
                }
            }
        }

        this.calculatePricing();

    }

    sanitiseItemQuantity() {

        let item_quantity = this.item_quantity;

        // if dp=0, only accept numbers
        if(this.item.quantity_decimal_places == 0 && /[^0-9]/.test(item_quantity)) {
            this.item.quantity = null;
        }
        // if dp > 0, only accept numbers/decimal points
        else if(this.item.quantity_decimal_places > 0 && /[^0-9.]/.test(item_quantity)) {
            this.item.quantity = null;
        }
        else if(this.item.quantity_decimal_places > 0 && item_quantity.startsWith('.')) {
            this.item.quantity = null;
        }
        else if(this.item.quantity_decimal_places > 0 && item_quantity.endsWith('.')) {
            this.item.quantity = null;
        }
        // if dp > 0, ensure decimal places not exceeded
        else if (this.item.quantity_decimal_places > 0 && item_quantity.includes('.') && item_quantity.split('.')[1].length > this.item.quantity_decimal_places) {
            this.item.quantity = null;
        }
        else if(!item_quantity.length) {
            this.item.quantity = 0;
        }
        else {
            this.item.quantity = Number(new Decimal(item_quantity).toDecimalPlaces(this.item.quantity_decimal_places));
        }

        this.calculatePricing();

    }

    sanitiseItemDiscountAmount() {

        let item_discount_amount = this.item_discount_amount.toString();

        // only accept numbers/decimal points
        if(/[^0-9.]/.test(item_discount_amount)) {
            this.item.discount_amount = null;
        }
        else if(item_discount_amount.startsWith('.')) {
            this.item.discount_amount = null;
        }
        else if(item_discount_amount.endsWith('.')) {
            this.item.discount_amount = null;
        }
        else if(item_discount_amount.split('.').length > 2) {
            this.item.discount_amount = null;
        }
        else if(!item_discount_amount.length) {
            this.item.discount_amount = 0;
        }
        else {

            if(this.item.discount_type == 'percentage') {
                this.item.discount_amount = Number(new Decimal(item_discount_amount).toDecimalPlaces(4));
                if(this.item.discount_amount > 100) {
                    this.item.discount_amount = 100;
                }
            }
            else if(this.item.discount_type == 'fixed_amount') {
                this.item.discount_amount = Number(new Decimal(item_discount_amount).toDecimalPlaces(2));
            }

        }

        this.calculatePricing();

    }

    adjustItemQuantity(adjustment) {

        if(!this.item_quantity || isNaN(this.item_quantity)) {
            this.item_quantity = 0;
        }

        if(adjustment == 'subtract') {
            if(this.item_quantity > 0) {
                this.item_quantity = Number(new Decimal(this.item_quantity).minus(this.item.quantity_step_interval).toDecimalPlaces(this.item.quantity_decimal_places));
                if(this.item_quantity < 0) {
                    this.item_quantity = 0;
                }
            }
        }
        else if (adjustment == 'add') {
            this.item_quantity = Number(new Decimal(this.item_quantity).plus(this.item.quantity_step_interval).toDecimalPlaces(this.item.quantity_decimal_places));
        }

        this.item.quantity = this.item_quantity;

        this.calculatePricing();
    }

    adjustItemDiscountType(type) {

        if(type == 'percentage') {
            this.item.discount_type = 'percentage';
            if(this.item.discount_amount > 100) {
                this.item.discount_amount = 100;
            }
        }
        else if(type == 'fixed_amount') {
            this.item.discount_type = 'fixed_amount';
        }

        this.calculatePricing();

    }

    async calculatePricing() {

        if(this.item.sell_price !== null && this.item.quantity !== null && this.item.discount_amount !== null) {

            // NOTE: If no custom price is set then check for volume discounts or contract prices
            if(!this.item['has_custom_price']) {

                this.item['has_contract_price'] = false;
                this.item['has_volume_discount'] = false;

                // NOTE: check if item has volume discounts
                if(this.item.type == 'product') { // Product
                    await this.productService.getProduct(this.item.id).then((product: any) => {
                        if(product) {
                            if(product['prices']) {
                                for(let i = 0; i < product['prices'].length; i++) {
                                    if(product['prices'][i].currency.id == this.cart.currency.id) {
                                        for(let j = 0; j < product['prices'][i].pricing_tiers.length; j++) {
                                            if(product['prices'][i].pricing_tiers[j].id == this.cart.pricing_tier.id) {
                                                if(this.item.quantity > 1) {
                                                    if(product['prices'][i].pricing_tiers[j].volume_discounts) {
                                                        for(let k = 0; k < product['prices'][i].pricing_tiers[j].volume_discounts.length; k++) {
                                                            if(this.item.quantity >= product['prices'][i].pricing_tiers[j].volume_discounts[k].min_quantity) {
                                                                this.item.sell_price = product['prices'][i].pricing_tiers[j].volume_discounts[k].sell_price;
                                                                this.item['has_volume_discount'] = true;
                                                            }
                                                            else {
                                                                this.item.sell_price = product['prices'][i].pricing_tiers[j].price;
                                                            }
                                                        }
                                                    }
                                                }
                                                else {
                                                    this.item.sell_price = product['prices'][i].pricing_tiers[j].price;
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    })
                    .catch(() => {});
                }
                else if(this.item.type == 'product_variant') { // Product variant

                    let aggregated_product_quantities = {};
                    let existing_product_variant_cart_items = [];

                    for(let i = 0; i < this.cart["items"].length; i++) {

                        // if product variant then create aggregated_product_quantities
                        if(this.cart["items"][i].type == 'product_variant') {

                            // push to existing_product_variant_cart_items for later use
                            existing_product_variant_cart_items.push(this.cart["items"][i]);

                            // if not in aggregated_product_quantities then add, otherwise increment
                            if(!aggregated_product_quantities[this.cart["items"][i]["product_id"]]) {
                                aggregated_product_quantities[this.cart["items"][i]["product_id"]] = this.cart["items"][i].quantity;
                            }
                            else {
                                aggregated_product_quantities[this.cart["items"][i]["product_id"]] = Number(new Decimal(aggregated_product_quantities[this.cart["items"][i]["product_id"]]).plus(new Decimal(this.cart["items"][i].quantity)));
                            }

                        }
                    }

                    await this.productService.getProductVariant(this.item.id).then(async (product_variant: any) => {
                        if(product_variant) {
                            if(product_variant['prices']) {
                                for(let i = 0; i < product_variant['prices'].length; i++) {
                                    if(product_variant['prices'][i].currency.id == this.cart.currency.id) {
                                        for(let j = 0; j < product_variant['prices'][i].pricing_tiers.length; j++) {
                                            if(product_variant['prices'][i].pricing_tiers[j].id == this.cart.pricing_tier.id) {

                                                if(product_variant['prices'][i].pricing_tiers[j].volume_discounts) {
                                                    for(let k = 0; k < product_variant['prices'][i].pricing_tiers[j].volume_discounts.length; k++) {
                                                        if(product_variant['prices'][i].pricing_tiers[j].volume_discounts[k].inherited && aggregated_product_quantities[product_variant["product_id"]] >= product_variant['prices'][i].pricing_tiers[j].volume_discounts[k].min_quantity || this.item.quantity >= product_variant['prices'][i].pricing_tiers[j].volume_discounts[k].min_quantity) {
                                                            this.item.sell_price = product_variant['prices'][i].pricing_tiers[j].volume_discounts[k].sell_price;
                                                            this.item['has_volume_discount'] = true;
                                                        }
                                                    }
                                                }

                                                if(!this.item['has_volume_discount']) {
                                                    this.item['sell_price'] = product_variant['prices'][i].pricing_tiers[j].price;
                                                }

                                            }
                                        }
                                    }
                                }
                            }
                        }
                    })
                    .catch(() => {});
                }
                else if(this.item.type == 'ticket_type') { // Ticket
                    await this.eventService.getTicketType(this.item.id).then((ticket_type: any) => {
                        if(ticket_type) {
                            if(ticket_type['prices']) {
                                for(let i = 0; i < ticket_type['prices'].length; i++) {
                                    if(ticket_type['prices'][i].currency.id == this.cart.currency.id) {
                                        for(let j = 0; j < ticket_type['prices'][i].pricing_tiers.length; j++) {
                                            if(ticket_type['prices'][i].pricing_tiers[j].id == this.cart.pricing_tier.id) {
                                                if(this.item.quantity > 1) {
                                                    if(ticket_type['prices'][i].pricing_tiers[j].volume_discounts) {
                                                        for(let k = 0; k < ticket_type['prices'][i].pricing_tiers[j].volume_discounts.length; k++) {
                                                            if(this.item.quantity >= ticket_type['prices'][i].pricing_tiers[j].volume_discounts[k].min_quantity) {
                                                                this.item.sell_price = ticket_type['prices'][i].pricing_tiers[j].volume_discounts[k].sell_price;
                                                                this.item['has_volume_discount'] = true;
                                                            }
                                                            else {
                                                                this.item.sell_price = ticket_type['prices'][i].pricing_tiers[j].price;
                                                            }
                                                        }
                                                    }
                                                }
                                                else {
                                                    this.item.sell_price = ticket_type['prices'][i].pricing_tiers[j].price;
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    })
                    .catch(() => {});
                }

                await this.storageService.get('organisation_use_contract_price_even_if_more_expensive').then((organisation_use_contract_price_even_if_more_expensive) => {

                    // NOTE: If organisation is assigned, check for relevant contract prices
                    if(this.cart['person'] && this.cart["organisation"] && this.cart["organisation"].contract_prices[this.cart.currency.id] && this.cart["organisation"].contract_prices[this.cart.currency.id][this.item.id]) {

                        // NOTE: overwrite sell price with contract price if setting is true, otherwise only if contract price is less than sell price
                        if(organisation_use_contract_price_even_if_more_expensive == 'true') {
                            this.item.sell_price = this.cart.organisation.contract_prices[this.cart.currency.id][this.item.id].sell_price;
                            this.item['has_contract_price'] = true;
                            this.item['has_volume_discount'] = false;
                        }
                        else {
                            if(this.cart.organisation.contract_prices[this.cart.currency.id][this.item.id].sell_price <= this.item.sell_price) {
                                this.item.sell_price = this.cart.organisation.contract_prices[this.cart.currency.id][this.item.id].sell_price;
                                this.item['has_contract_price'] = true;
                                this.item['has_volume_discount'] = false;
                            }
                        }

                    }

                })
                .catch(() => {});

                this.item_sell_price = this.item.sell_price;

            }

            this.item.sell_subtotal_amount = Number(new Decimal(this.item.sell_price).times(this.item.quantity).toDecimalPlaces(2));

            if(this.item.comparison_price) {
                this.item.comparison_price_percentage_difference = Number(new Decimal(this.item.comparison_price).minus(new Decimal(this.item.sell_price)).dividedBy(new Decimal(this.item.comparison_price)).times(100).toDecimalPlaces(2));
                if(this.item.comparison_price_percentage_difference < 0) {
                    this.item.comparison_price_percentage_difference = 0;
                }
            }

            if(this.item.discount_type == 'percentage') {
                this.item.discount_price = Number(new Decimal(this.item.sell_subtotal_amount).minus(new Decimal(Number(new Decimal(this.item.sell_subtotal_amount).times(new Decimal(1).minus(new Decimal(this.item.discount_amount).dividedBy(100))).toDecimalPlaces(2)))).toDecimalPlaces(2));
            }
            else if (this.item.discount_type == 'fixed_amount') {
                this.item.discount_price = this.item.discount_amount;
                if(this.item.discount_price > this.item.sell_subtotal_amount) {
                    this.item.discount_price = this.item.sell_subtotal_amount;
                }
            }

            this.item.total_price = Number(new Decimal(this.item.sell_subtotal_amount).minus(this.item.discount_price).toDecimalPlaces(2));

            if(this.item.cost_price) {

                this.item.cost_subtotal_amount = Number(new Decimal(this.item.cost_price).times(new Decimal(this.item.quantity)).toDecimalPlaces(2));

                if(this.cart.pricing_tier.tax_inclusive) {

                    this.item.tax_rate = 0;

                    if(this.item.tax_type == 'standard_rate') {
                        this.item.tax_rate = this.cart.tax_zone.standard_tax_rate;
                    }
                    else if(this.item.tax_type == 'reduced_rate') {
                        this.item.tax_rate = this.cart.tax_zone.reduced_tax_rate;
                    }

                    this.item.inclusive_tax_price = Number(new Decimal(this.item.total_price).minus(new Decimal(this.item.total_price).dividedBy(new Decimal(this.item.tax_rate).dividedBy(100).plus(1))).toDecimalPlaces(2));

                }
                else {
                    this.item.inclusive_tax_price = 0;
                }

                this.item.total_price_excluding_tax = Number(new Decimal(this.item.total_price).minus(new Decimal(this.item.inclusive_tax_price)).toDecimalPlaces(2));

                this.item.profit = Number(new Decimal(this.item.total_price_excluding_tax).minus(new Decimal(this.item.cost_subtotal_amount)).toDecimalPlaces(2));

                this.item.margin = Number(new Decimal(new Decimal(this.item.profit).dividedBy(new Decimal(this.item.total_price_excluding_tax)).times(100).toDecimalPlaces(2)));

            }

        }

    }

    togglePriceBreakdown() {
        if(this.auto_hide_price_breakdown == true) {
            this.auto_hide_price_breakdown = false;
        }
        else if(this.auto_hide_price_breakdown == false) {
            this.auto_hide_price_breakdown = true;
        }
    }

    async enterPressed() {

        // NOTE: only confirm when the check button would be shown
        if(this.item.sell_price !== null && this.item.quantity !== null && this.item.discount_amount !== null) {

            // NOTE: wait for any volume discount/contract price lookup triggered by the last keystroke to finish
            await this.calculatePricing();

            this.confirmationClicked();

        }

    }

    confirmationClicked() {
        if (this.item.quantity <= 0) {
            this.update();
        }
        else if (this.item.type == 'product') { // Product
            this.productService.getProduct(this.item.id).then((product: any) => {
                if (product) {
                    if (product.track_stock == true && (product.available_stock <= 0 || product.available_stock - this.item.quantity < 0)) {
                        if (!this.permissionService.isPermitted('pos-sell-oversell-item')) {
                            this.presentAlert('Insufficient permissions', "You don't have permission to oversell this item.");
                        }
                        else {
                            this.storageService.get("alert_if_overselling_item").then((alert_if_overselling_item) => {
                                if (alert_if_overselling_item == true) {
                                    this.presentOversellingProductConfirmation();
                                }
                                else {
                                    this.update();
                                }
                            })
                            .catch((error) => {
                                console.error(error)
                            });
                        }
                    }
                    else {
                        this.update();
                    }
                }
            })
            .catch((error) => {
                console.error(error)
            });
        }
        else if (this.item.type == 'product_variant') { // Product variant
            this.productService.getProductVariant(this.item.id).then((product_variant: any) => {
                if (product_variant) {
                    if (product_variant.track_stock == true && (product_variant.available_stock <= 0 || product_variant.available_stock - this.item.quantity < 0)) {
                        if (!this.permissionService.isPermitted('pos-sell-oversell-item')) {
                            this.presentAlert('Insufficient permissions', "You don't have permission to oversell this item.");
                        }
                        else {
                            this.storageService.get("alert_if_overselling_item").then((alert_if_overselling_item) => {
                                if (alert_if_overselling_item == true) {
                                    this.presentOversellingProductConfirmation();
                                }
                                else {
                                    this.update();
                                }
                            })
                            .catch((error) => {
                                console.error(error)
                            });
                        }
                    }
                    else {
                        this.update();
                    }
                }
            })
            .catch((error) => {
                console.error(error)
            });
        }
        else if (this.item.type == 'ticket_type') { // Ticket type
            this.eventService.getTicketType(this.item.id).then((ticket_type: any) => {
                if (ticket_type) {
                    if (ticket_type.tickets_left_count && (ticket_type.tickets_left_count <= 0 || ticket_type.tickets_left_count - this.item.quantity < 0)) {
                        if (!this.permissionService.isPermitted('pos-sell-oversell-item')) {
                            this.presentAlert('Insufficient permissions', "You don't have permission to oversell this item.");
                        }
                        else {
                            this.storageService.get("alert_if_overselling_item").then((alert_if_overselling_item) => {
                                if (alert_if_overselling_item == true) {
                                    this.presentOversellingTicketConfirmation();
                                }
                                else {
                                    this.update();
                                }
                            })
                            .catch((error) => {
                                console.error(error)
                            });
                        }
                    }
                    else {
                        this.update();
                    }
                }
            })
            .catch((error) => {
                console.error(error)
            });
        }
        else {
            this.update();
        }
    }

    update() {
        if(this.item.quantity <= 0) {

            this.cartService.deleteItem(this.item.id).then(() => {
                this.modalController.dismiss();
            });

        }
        else if(this.save_contract_price && this.canSaveContractPrice()) {

            if(!this.is_saving_contract_price) {

                this.is_saving_contract_price = true;

                // NOTE: on failure the service alerts and the modal stays open so the contract price can be retried or unticked
                this.saveContractPrice().then(() => {
                    this.updateItem();
                })
                .catch(() => {
                    this.is_saving_contract_price = false;
                });

            }

        }
        else {

            this.updateItem();

        }
    }

    updateItem() {

        delete this.item.comparison_price_percentage_difference;
        delete this.item.discount_price;
        delete this.item.profit;
        delete this.item.inclusive_tax_price;

        this.cartService.updateItem(this.item).then(async () => {
            this.modalController.dismiss();
        });

    }

    saveContractPrice() {
        return new Promise<void>((resolve, reject) => {

            let existing_contract_price = this.cart?.organisation?.contract_prices?.[this.cart?.currency?.id]?.[this.item?.id];
            let save_promise = null;

            if(existing_contract_price) {
                save_promise = this.organisationService.updateOrganisationContractPrice(this.cart.organisation.id, existing_contract_price.id, { fixed_price: this.item.sell_price });
            }
            else {
                let data = { type: 'fixed_price', currency_id: this.cart.currency.id, fixed_price: this.item.sell_price };
                if(this.item.type == 'product') {
                    data['product_id'] = this.item.id;
                }
                else if(this.item.type == 'product_variant') {
                    data['product_variant_id'] = this.item.id;
                }
                save_promise = this.organisationService.addOrganisationContractPrice(this.cart.organisation.id, data);
            }

            save_promise.then((contract_price: any) => {

                let cached_contract_price = { id: contract_price.id, type: contract_price.type, sell_price: Number(contract_price.fixed_price) };

                // NOTE: flag the stored organisation so future carts on this till load its contract prices
                this.organisationService.updateOrganisationHasContractPrices(this.cart.organisation.id).then(() => {}).catch(() => {});

                this.cartService.updateOrganisationContractPrice(this.item.id, cached_contract_price).then(() => {
                    this.cart.organisation.contract_prices[this.cart.currency.id] = this.cart.organisation.contract_prices[this.cart.currency.id] || {};
                    this.cart.organisation.contract_prices[this.cart.currency.id][this.item.id] = cached_contract_price;
                    this.item['has_contract_price'] = true;
                    this.item['has_custom_price'] = false;
                    resolve();
                })
                .catch(() => {
                    reject();
                });

            })
            .catch(() => {
                reject();
            });

        });
    }

    async presentAlert(header, message) {
        const alert = await this.alertController.create({
            header: header,
            message: message,
            buttons: ["OK"],
        });
        await alert.present();
    }

    async presentOversellingProductConfirmation() {
        const alert = await this.alertController.create({
            header: 'No available stock left',
            message: 'Are you sure you want to oversell this item?',
            buttons: [
                {
                    text: 'Cancel',
                    role: 'cancel'
                },
                {
                    text: 'Yes',
                    handler: () => {
                        this.update();
                    }
                }
            ],
        });
        await alert.present();
    }

    async presentOversellingTicketConfirmation() {
        const alert = await this.alertController.create({
            header: 'No tickets left',
            message: 'Are you sure you want to oversell this item?',
            buttons: [
                {
                    text: 'Cancel',
                    role: 'cancel'
                },
                {
                    text: 'Yes',
                    handler: () => {
                        this.update();
                    }
                }
            ],
        });
        await alert.present();
    }

    close() {
        this.modalController.dismiss();
    }

}
